'use client';

import { forwardRef } from 'react';
import type { CloseButtonProps as MantineCloseButtonProps, ElementProps } from '@mantine/core';
import { CloseButton as MantineCloseButton } from '@mantine/core';

import type { LockedProps } from '../types';

// Mantine's CloseButtonProps carries none of the button element's own props (it's polymorphic), so
// handlers like `onClick`/`onMouseDown` have to be intersected back in.
export type CloseButtonProps = Omit<MantineCloseButtonProps, LockedProps> &
  Omit<ElementProps<'button'>, 'className' | 'style'>;

/**
 * ### CloseButton
 *
 * @description
 * Mantine's CloseButton minus per-call-site style overrides (see {@link LockedProps}). A bare ✕
 * affordance for dismissing something the surrounding component owns — a pill, a chip, an inline
 * filter. `Modal` and `Drawer` render their own, so don't add one there.
 *
 * Mantine's renders an unnamed icon-only button, so this one defaults `aria-label` to "Close" —
 * override it whenever the target needs naming ("Remove Colour"). For a general icon-only action,
 * use `ActionIcon` instead.
 *
 * @example
 * ```tsx
 * <CloseButton aria-label="Remove Colour" onMouseDown={onRemove} variant="transparent" />
 * ```
 */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>((props, ref) => {
  return <MantineCloseButton ref={ref} aria-label="Close" {...props} />;
});

CloseButton.displayName = 'CloseButton';
